import { isHttpClientError } from "@/http";
import type { DatasetLabel, VideoResolution } from "@/api";
import { isDatasetWriteForbidden } from "@/api/dataset";
import { labelDisplayText } from "@/components/label";
import { storageBackendErrorText } from "./storageBackendText";

export function formatEmptyDatasetMessage({
  hasSearchQuery,
  searchQuery,
  hasFilterSelection,
}: {
  hasSearchQuery: boolean;
  searchQuery: string;
  hasFilterSelection: boolean;
}): string {
  if (hasSearchQuery && hasFilterSelection) {
    return `검색어 "${searchQuery}"와 선택한 태그에 일치하는 Dataset이 없습니다. 검색어나 필터를 바꿔 보세요.`;
  }
  if (hasSearchQuery) {
    return `검색어 "${searchQuery}"와 일치하는 Dataset이 없습니다. 검색어를 바꿔 보세요.`;
  }
  if (hasFilterSelection) {
    return "선택한 태그와 일치하는 Dataset이 없습니다. 필터를 바꿔 보세요.";
  }
  return "아직 등록된 Dataset이 없습니다. 새 Dataset을 추가해 주세요.";
}

export function formatSchemaVersion(schemaVersion: string): string {
  return schemaVersion.replace(/_/g, " ");
}

// 서버 detail 은 영어 개발자 문구다. 사용자가 사유를 읽어야 하는 코드는 여기서 한국어로 바꾼다.
export function resolveErrorMessage(error: unknown): string {
  const storageBackendText = storageBackendErrorText(error);
  if (storageBackendText !== null) {
    return storageBackendText;
  }
  if (isDatasetWriteForbidden(error)) {
    return "이 Dataset은 만든 사람이나 관리자만 수정할 수 있습니다. 설정 변경은 검토 요청으로 올려 주세요.";
  }
  if (isHttpClientError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Dataset 요청을 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.";
}

// 목록 표의 라벨 칩 문구. 표기 규칙은 labelDisplay 가 소유한다. 카드와 같은 문구를 쓴다.
export function formatDatasetLabel(label: DatasetLabel): string {
  return labelDisplayText(label.key, label.value);
}

// 에피소드 길이(초) 표시. 값이 없거나 음수이면 null 을 반환하고, 호출부는 그 자리를 비운다.
// 표시 정밀도로 먼저 반올림한 뒤 분 경계를 판정한다. 그래서 59.96 이 "60.0초"로 표시되지 않는다.
export function formatEpisodeDuration(seconds: number | null): string | null {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  const rounded = Math.round(seconds * 10) / 10;
  if (rounded < 60) {
    // 짧은 에피소드는 정수로 자르면 길이 차이가 보이지 않는다. 그래서 소수 첫째 자리까지 표시한다.
    return `${rounded.toFixed(1)}초`;
  }
  const total = Math.round(rounded);
  const minutes = Math.floor(total / 60);
  const restSeconds = total % 60;
  return restSeconds === 0 ? `${minutes}분` : `${minutes}분 ${restSeconds}초`;
}

// 축(embodiment/arms/end_effector)의 distinct 값 리스트를 표시 문자열로 바꾼다.
// 리스트는 BE 가 계산하고 정렬하며, sentinel 을 제외한 값이다.
//   - 값이 없으면 null("-")
//   - 하나면 그 값
//   - 여러 개면 " / " 로 이은 실제 값. 뜻이 불분명한 "Mixed" 대신 쓴다.
// 목록 하드웨어 셀과 상세 Overview 가 함께 쓴다.
export function formatSourceAxisValues(values: readonly string[]): string | null {
  return values.length === 0 ? null : values.join(" / ");
}

// 카메라 영상 해상도 표시. 여러 개면 하드웨어 축과 같이 " / " 로 실제 값을 나열한다.
// 모르면 `—` 대신 "정보 없음" 을 쓴다. 이 값은 칩 안에서 채널 이름 옆에 표시되므로
// 기호만으로는 뜻을 알 수 없다.
export function formatVideoResolutions(resolutions: readonly VideoResolution[]): string {
  if (resolutions.length === 0) {
    return "정보 없음";
  }
  return resolutions.map(({ width, height }) => `${width}×${height}`).join(" / ");
}
